import { createAsyncThunk } from '@reduxjs/toolkit'
import { instance } from '../api/api'
import { CategoriesType, GetCategoriesResponseType } from './types'

export const getCategories = createAsyncThunk(
  'categories/getCategories',
  async (
    {
      token,
      page,
      name,
      search,
      itemsPerPage,
      description,
      menuId,
    }: {
      token: string
      page: number
      name?: string
      search?: string
      itemsPerPage: number
      description?: string
      menuId: string
    },
    thunkAPI,
  ) => {
    try {
      const response = await instance.get<GetCategoriesResponseType>('category', {
        params: {
          ...(page ? { page: page } : {}),
          ...(itemsPerPage ? { itemsPerPage: itemsPerPage } : {}),
          ...(search ? { search: search } : {}),
          ...(name ? { name: name } : {}),
          ...(description ? { description: description } : {}),
          menuId: menuId,
        },
        headers: { authorization: `Bearer ${token}` },
      })

      return response.data
    } catch (error: any) {
      if (error?.response?.status === 401) {
        // Handle 401 error (e.g., redirect to login, notify user)
        return thunkAPI.rejectWithValue('Unauthorized')
      }

      // Handle other errors
      return thunkAPI.rejectWithValue(error?.response?.data?.message || 'An error occurred.')
    }
  },
)

export const getPublicCategories = createAsyncThunk(
  'categories/getPublicCategories',
  async (
    {
      companyId,
    }: {
      companyId: string
    },
    thunkAPI,
  ) => {
    try {
      const response = await instance.get<CategoriesType[]>(
        `category-public/company/${companyId}/products`,
        {
          params: {
            ...{ page: 1 },
            ...{ itemsPerPage: 1000 },
          },
        },
      )

      return response.data
    } catch (error: any) {
      if (error?.response?.status === 401) {
        // Handle 401 error (e.g., redirect to login, notify user)
        return thunkAPI.rejectWithValue('Unauthorized')
      }

      // Handle other errors
      return thunkAPI.rejectWithValue(error?.response?.data?.message || 'An error occurred.')
    }
  },
)

export const getCategoryFromCompany = createAsyncThunk(
  'categories/getCategoriesFromCompany',
  async (
    {
      token,
      page,
      itemsPerPage,
      search,
    }: {
      token: string
      page: number
      itemsPerPage: number
      search?: string
    },
    thunkAPI,
  ) => {
    try {
      const response = await instance.get<GetCategoriesResponseType>('category', {
        params: {
          ...(page ? { page: page } : {}),
          ...(itemsPerPage ? { itemsPerPage: itemsPerPage } : {}),
          ...(search ? { search: search } : {}),
        },
        headers: { authorization: `Bearer ${token}` },
      })

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

export const getCurrentCategory = createAsyncThunk(
  'categories/getCurrentCategory',
  async ({ categoryId, token }: { categoryId: string | undefined; token: string }, thunkAPI) => {
    try {
      const response = await instance.get<CategoriesType>(`category/${categoryId}`, {
        headers: { authorization: `Bearer ${token}` },
      })

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.message)
    }
  },
)

export const createCategory = createAsyncThunk(
  'categories/createCategory',
  async ({ data, token, menuId }: { data: any; token: string; menuId: string }, thunkAPI) => {
    try {
      const response = await instance.post<any>(`menu/${menuId}/categories`, data, {
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${token}`,
        },
        params: {
          menuId: menuId,
        },
      })

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || 'Something went wrong')
    }
  },
)

export const deleteCurrentFileFromCategory = createAsyncThunk(
  'categories/deleteCurrentFileFromCategory',
  async (
    {
      categoryId,
      filesToDelete,
      token,
    }: { categoryId: string | undefined; filesToDelete: string[]; token: string },
    thunkAPI,
  ) => {
    try {
      const response = await instance.delete<CategoriesType>(`category/${categoryId}/files`, {
        data: { filesToDelete },
        headers: { authorization: `Bearer ${token}` },
      })

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

export const pushNewFilesToCategory = createAsyncThunk(
  'categories/pushNewImagesToCategory',
  async (
    {
      categoryId,
      formData,
      token,
    }: { categoryId: string | undefined; formData: any; token: string },
    thunkAPI,
  ) => {
    try {
      const response = await instance.put<CategoriesType>(`category/${categoryId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data; charset=utf-8',
          authorization: `Bearer ${token}`,
        },
      })

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.message)
    }
  },
)

export const changeCategoryMainInformation = createAsyncThunk(
  'categories/changeCategoryMainInformation',
  async (
    {
      token,
      categoryId,
      name,
      description,
      type,
    }: {
      categoryId: string | number
      name: string
      description: string
      token: string
      type?: string
    },
    thunkAPI,
  ) => {
    try {
      const response = await instance.patch<CategoriesType>(
        `category/${categoryId}`,
        {
          description,
          name,
          ...(type && { type }),
        },
        {
          headers: { authorization: `Bearer ${token}` },
        },
      )

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

export const changeCategoryDescription = createAsyncThunk(
  'categories/changeCategoryDescription',
  async (
    {
      categoryId,
      description,
      token,
    }: { categoryId: string | number | undefined; description: string; token: string },
    thunkAPI,
  ) => {
    try {
      const response = await instance.patch<CategoriesType>(
        `category/${categoryId}`,
        {
          description,
        },
        {
          headers: { authorization: `Bearer ${token}` },
        },
      )

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

export const deleteCurrentCategory = createAsyncThunk(
  'categories/deleteCurrentCategory',
  async ({ categoryId, token }: { categoryId: string | undefined; token: string }, thunkAPI) => {
    try {
      const response = await instance.delete<CategoriesType>(`category/${categoryId}`, {
        headers: { authorization: `Bearer ${token}` },
      })

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

export const blockCategory = createAsyncThunk(
  'categories/blockCategories',
  async (
    {
      categoryId,
      isBlocked,
    }: {
      categoryId: string | number | undefined
      isBlocked: boolean
    },
    thunkAPI,
  ) => {
    try {
      const response = await instance.patch<any>(`category/${categoryId}`, {
        isBlocked,
      })

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)
